import React from 'react';
import { RepairOrderPing, RepairOrderPingType } from '../types/repairOrders';
import PermissionsContainer from './PermissionsContainer';
import { formatPingAge } from '../utils/pingAge';
import { isFinishedRepairOrder } from '../utils/repairOrderStatus';

/** Chip label for each ping type. */
const PING_LABELS: Record<string, string> = {
  eta: 'ETA requested',
  completion: 'Completion requested',
};

/**
 * The two request affordances, in display order. The label doubles as the
 * accessible name — these sit inside a `role="button"` card in a narrow drawer,
 * so the visible text has to be short while still saying what is requested.
 */
const PING_REQUESTS: { type: RepairOrderPingType; label: string; short: string }[] = [
  { type: 'eta', label: 'Request vendor ETA', short: 'Request ETA' },
  { type: 'completion', label: 'Request completion status', short: 'Request status' },
];

export interface RepairOrderPingControlsProps {
  /** Open pings on the repair order. Chips render for each, ungated. */
  pings: RepairOrderPing[] | undefined;
  /**
   * Opts the surface into the REQUEST buttons. Chips render regardless — an
   * outstanding request is information, and hiding it from an un-granted user
   * is worse than the email this feature replaces.
   */
  allowRequest?: boolean;
  /**
   * The repair order's `orderStatus`. A finished one (Completed / Closed /
   * Cancelled) loses its REQUEST buttons — there is no ETA left to ask a vendor
   * for — but KEEPS its chips, because an outstanding request is still a record
   * of someone having asked.
   *
   * Undefined FAILS OPEN (buttons shown): a caller that could not determine the
   * status must not silently strip a working feature.
   */
  orderStatus?: string | null;
  /** Asks maintenance for that type. The caller owns optimism + the toast. */
  onRequest?: (requestType: RepairOrderPingType) => void;
  /**
   * Repeated instances share one componentId; pass true on exactly one of them
   * so permissions mode decorates a single handle.
   */
  showHandle?: boolean;
  /**
   * 'compact' is the card idiom (0.625rem chips) used by the RO cards and the
   * Trouble Tickets panel; 'roomy' is for the modal, which has the space.
   */
  size?: 'compact' | 'roomy';
}

/**
 * Open maintenance-ping chips plus the "Request ETA" / "Request status"
 * buttons for ONE repair order.
 *
 * Shared by every surface that offers the control — the repair-order cards
 * (Pro ROs sub-tab, Equipment drawer), the Trouble Tickets panel card and the
 * Trouble Ticket edit modal — so the gate id, the labels, the chip text and the
 * click/keyboard propagation rules cannot drift between them.
 *
 * Renders NOTHING when there is neither a chip to show nor a type left to ask
 * about, so callers can drop it in unconditionally.
 */
const RepairOrderPingControls: React.FC<RepairOrderPingControlsProps> = ({
  pings,
  allowRequest = false,
  orderStatus,
  onRequest,
  showHandle = false,
  size = 'compact',
}) => {
  const open = pings || [];
  const openTypes = new Set(open.map((p) => p.requestType));
  // The chips are deliberately NOT gated on this — only the buttons are.
  const finished = isFinishedRepairOrder(orderStatus);
  const offered =
    allowRequest && !finished ? PING_REQUESTS.filter((r) => !openTypes.has(r.type)) : [];

  const chipText = size === 'roomy' ? 'text-xs' : 'text-[0.625rem]';
  const buttonText = size === 'roomy' ? 'text-xs' : 'text-[0.625rem]';
  const buttonPad = size === 'roomy' ? 'px-2 py-1' : 'px-1.5 py-0.5';

  return (
    <>
      {open.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mt-0.5">
          {open.map((p) => (
            <span
              key={p.recno}
              data-testid="ro-ping-chip"
              className={`inline-flex items-center rounded-full ${chipText} font-medium px-1.5 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200`}
            >
              {[
                PING_LABELS[p.requestType] || 'Requested',
                formatPingAge(p.requestedAt),
                p.requestedBy,
              ].filter(Boolean).join(' · ')}
            </span>
          ))}
        </div>
      )}
      {/* Nothing left to ask for — the chips already say so. */}
      {offered.length > 0 && (
        <PermissionsContainer
          componentId="repair-order-ping-request"
          componentName="Request Maintenance ETA"
          defaultHidden
          showHandle={showHandle}
        >
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {offered.map((r) => (
              <button
                key={r.type}
                type="button"
                aria-label={r.label}
                title={r.label}
                data-testid={`ro-ping-request-${r.type}`}
                // On the card surfaces the whole card is a role="button" that
                // opens a drawer / the ticket modal — without these, requesting
                // a ping would also open it. Keyboard included: Enter/Space on
                // an inner <button> bubble to the card's onKeyDown.
                onClick={(e) => {
                  e.stopPropagation();
                  onRequest?.(r.type);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                className={`inline-flex items-center rounded-full ${buttonText} font-medium ${buttonPad} border border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors`}
              >
                {r.short}
              </button>
            ))}
          </div>
        </PermissionsContainer>
      )}
    </>
  );
};

export default RepairOrderPingControls;
